import { reactive, shallowRef, watch } from 'vue'
import { storeToRefs } from 'pinia'
import type { GcomeApPerfStatus, GcomeApReportRate, KeyboardPerfCfg } from '@rapoo/device-sdk'
import { useDeviceStore } from '@/stores'
import { defaultReportError } from '@/composables/useErrorLogger'
import type { KeyboardPerformanceToggle, KeyboardPerformanceToggleChange } from '../types'

type ReportRateLabel = '1K' | '2K' | '4K' | '8K' | '16K'
type TickRateValue = '0' | '1' | '2'

const REPORT_RATE_CODES: Record<ReportRateLabel, GcomeApReportRate> = {
  '16K': 0,
  '1K': 4,
  '2K': 3,
  '4K': 2,
  '8K': 1,
}

/** 正式性能页当前只开放 1K-16K；低速 EP 码仍由 SDK/协议完整保留。 */
const REPORT_RATE_LABELS: Partial<Record<GcomeApReportRate, ReportRateLabel>> = {
  0: '16K',
  1: '8K',
  2: '4K',
  3: '2K',
  4: '1K',
}

const TICK_RATE_CODES: Record<TickRateValue, number> = {
  '0': 0,
  '1': 1,
  '2': 2,
}

const TICK_RATE_VALUES: Record<number, TickRateValue> = {
  0: '0',
  1: '1',
  2: '2',
}

function isGcomeApPerfStatus(
  config: KeyboardPerfCfg | GcomeApPerfStatus,
): config is GcomeApPerfStatus {
  return 'tickRate' in config || 'lockWin' in config || 'macMode' in config
}

export function useKeyboardPerformance() {
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)

  const isLoading = shallowRef(false)
  const toggleValues = reactive<Record<KeyboardPerformanceToggle, boolean>>({
    winLock: false,
    fullAntiGhosting: false,
    adaptiveCalibration: false,
    bottomOutStabilization: false,
    macMode: false,
    swapWASD: false,
    lockAltTab: false,
    lockAltF4: false,
  })
  const reportRate = shallowRef<ReportRateLabel>('2K')
  const debounceLevel = shallowRef('0')
  const tickRate = shallowRef<TickRateValue>('0')

  let loadToken = 0
  let writeQueue: Promise<void> = Promise.resolve()

  function getPerfCfgFeature() {
    return currentDevice.value?.features.keyboardPerfCfg
  }

  function enqueueWrite(task: () => Promise<void>) {
    writeQueue = writeQueue.then(task, task)
    return writeQueue
  }

  function applyDeviceConfig(config: GcomeApPerfStatus) {
    if (config.usbReportRate !== undefined) {
      reportRate.value = REPORT_RATE_LABELS[config.usbReportRate] ?? reportRate.value
    }
    if (config.tickRate !== undefined && config.tickRate in TICK_RATE_VALUES) {
      tickRate.value = TICK_RATE_VALUES[config.tickRate]
    }
    if (config.debounceLevel !== undefined) {
      debounceLevel.value = String(config.debounceLevel)
    }
    if (config.lockWin !== undefined) toggleValues.winLock = config.lockWin
    if (config.fullKeyRollover !== undefined) {
      toggleValues.fullAntiGhosting = config.fullKeyRollover
    }
    if (config.lockAltTab !== undefined) toggleValues.lockAltTab = config.lockAltTab
    if (config.lockAltF4 !== undefined) toggleValues.lockAltF4 = config.lockAltF4
    if (config.touchBottomTrigger !== undefined) {
      toggleValues.bottomOutStabilization = config.touchBottomTrigger
    }
    if (config.adaptiveCalibration !== undefined) {
      toggleValues.adaptiveCalibration = config.adaptiveCalibration
    }
    if (config.macMode !== undefined) toggleValues.macMode = config.macMode
    if (config.swapWasd !== undefined) toggleValues.swapWASD = config.swapWasd
  }

  async function loadFromDevice() {
    const token = ++loadToken
    if (!getPerfCfgFeature()) {
      isLoading.value = false
      return
    }

    isLoading.value = true
    try {
      await writeQueue
      if (token !== loadToken) return

      const feature = getPerfCfgFeature()
      if (!feature) return

      const config = await feature.getAllPerfCfg()
      if (token !== loadToken || !isGcomeApPerfStatus(config)) return
      applyDeviceConfig(config)
    } catch (error) {
      console.error('Failed to load keyboard performance settings', error)
      defaultReportError(`Failed to load keyboard performance settings: ${String(error)}`)
    } finally {
      if (token === loadToken) isLoading.value = false
    }
  }

  async function writeSetting<T>(
    action: (feature: NonNullable<ReturnType<typeof getPerfCfgFeature>>) => Promise<T>,
    apply: (result: T) => void,
  ) {
    const feature = getPerfCfgFeature()
    if (!feature) return

    await enqueueWrite(async () => {
      try {
        const result = await action(feature)
        apply(result)
      } catch (error) {
        console.error('Failed to update keyboard performance setting', error)
        defaultReportError(`Failed to update keyboard performance setting: ${String(error)}`)
      }
    })
  }

  function setToggleSetting({ id, value }: KeyboardPerformanceToggleChange) {
    const actions: Record<KeyboardPerformanceToggle, () => Promise<void>> = {
      winLock: () =>
        writeSetting(
          (feature) => feature.setLockWin(value),
          () => {
            toggleValues.winLock = value
          },
        ),
      fullAntiGhosting: () =>
        writeSetting(
          (feature) => feature.setFullKeyRollover(value),
          () => {
            toggleValues.fullAntiGhosting = value
          },
        ),
      adaptiveCalibration: () =>
        writeSetting(
          (feature) => feature.setAdaptiveCalibration(value),
          () => {
            toggleValues.adaptiveCalibration = value
          },
        ),
      bottomOutStabilization: () =>
        writeSetting(
          (feature) => feature.setTouchBottomTrigger(value),
          () => {
            toggleValues.bottomOutStabilization = value
          },
        ),
      macMode: () =>
        writeSetting(
          (feature) => feature.setMacMode(value),
          () => {
            toggleValues.macMode = value
          },
        ),
      swapWASD: () =>
        writeSetting(
          async (feature) => {
            await feature.setSwapWasd(value)
            return feature.getSwapWasd()
          },
          (actual) => {
            toggleValues.swapWASD = actual
          },
        ),
      lockAltTab: () =>
        writeSetting(
          (feature) => feature.setLockAltTab(value),
          () => {
            toggleValues.lockAltTab = value
          },
        ),
      lockAltF4: () =>
        writeSetting(
          (feature) => feature.setLockAltF4(value),
          () => {
            toggleValues.lockAltF4 = value
          },
        ),
    }

    return actions[id]()
  }

  function setReportRate(value: string) {
    if (!(value in REPORT_RATE_CODES)) return
    const label = value as ReportRateLabel
    return writeSetting(
      (feature) => feature.setReportRate(REPORT_RATE_CODES[label]),
      () => {
        reportRate.value = label
      },
    )
  }

  function setDebounceLevel(value: string) {
    const level = Number(value)
    if (!Number.isInteger(level) || level < 0 || level > 7) return
    return writeSetting(
      (feature) => feature.setDebounceLevel(level),
      () => {
        debounceLevel.value = value
      },
    )
  }

  function setTickRate(value: string) {
    if (!(value in TICK_RATE_CODES)) return
    const next = value as TickRateValue
    return writeSetting(
      (feature) => feature.setTickRate(TICK_RATE_CODES[next]),
      () => {
        tickRate.value = next
      },
    )
  }

  watch(currentDevice, loadFromDevice, { immediate: true })

  return {
    isLoading,
    toggleValues,
    reportRate,
    debounceLevel,
    tickRate,
    setToggleSetting,
    setReportRate,
    setDebounceLevel,
    setTickRate,
  }
}
